import { useMemo } from "react";
import {
  Copy,
  Mail,
  MessageCircle,
  Send,
  Share2,
  Instagram,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function encodeText(text = "") {
  return encodeURIComponent(text);
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = value;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

export default function ShareDialog({
  open,
  onOpenChange,
  title,
  text,
  url,
  imageHint = "",
}) {
  const shareText = useMemo(() => {
    const parts = [text, url].filter(Boolean);
    return parts.join(" ");
  }, [text, url]);

  const whatsappUrl = `https://wa.me/?text=${encodeText(shareText)}`;
  const telegramUrl = `https://t.me/share/url?url=${encodeText(url)}&text=${encodeText(text || title || "")}`;
  const mailtoUrl = `mailto:?subject=${encodeText(title || "Lord of the Quiz")}&body=${encodeText(shareText)}`;

  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast.info("Seu navegador não oferece o compartilhamento nativo aqui.");
      return;
    }

    try {
      await navigator.share({ title, text, url });
    } catch (error) {
      if (error?.name !== "AbortError") {
        toast.error("Não foi possível abrir o compartilhamento nativo.");
      }
    }
  };

  const handleCopy = async (message = "Link copiado!") => {
    try {
      await copyText(url);
      toast.success(message);
    } catch (error) {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const openExternal = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border border-purple-500/20 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-nunito font-bold text-white text-xl">
            Compartilhar
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Compartilhe este link em seus apps preferidos. A capa exibida será a do evento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-zinc-900/80 p-4">
            <p className="font-nunito font-bold text-white text-sm">{title}</p>
            <p className="text-zinc-400 text-xs mt-1 break-all">{url}</p>
            {imageHint ? (
              <p className="text-purple-300 text-xs mt-3">{imageHint}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleNativeShare}
              className="bg-purple-600 text-white hover:bg-purple-700 font-nunito font-bold"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartilhar…
            </Button>

            <Button
              onClick={() => openExternal(whatsappUrl)}
              variant="outline"
              className="border-green-500/30 text-green-300 hover:bg-green-500/10"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>

            <Button
              onClick={() => openExternal(telegramUrl)}
              variant="outline"
              className="border-sky-500/30 text-sky-300 hover:bg-sky-500/10"
            >
              <Send className="w-4 h-4 mr-2" />
              Telegram
            </Button>

            <Button
              onClick={() => {
                window.location.href = mailtoUrl;
              }}
              variant="outline"
              className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
            >
              <Mail className="w-4 h-4 mr-2" />
              E-mail
            </Button>

            <Button
              onClick={() => handleCopy("Link copiado para você colar no Instagram.")}
              variant="outline"
              className="border-pink-500/30 text-pink-300 hover:bg-pink-500/10"
            >
              <Instagram className="w-4 h-4 mr-2" />
              Instagram
            </Button>

            <Button
              onClick={() => handleCopy()}
              variant="outline"
              className="border-white/10 text-zinc-200 hover:bg-white/5"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
